import React, { useLayoutEffect, useRef } from "react";
import { cn } from "@/utils/cn";

interface PageHeaderProps {
  children: React.ReactNode;
  className?: string;
}

const PageHeaderRoot: React.FC<PageHeaderProps> = ({ children, className }) => {
  return (
    <div data-page-header="" className={cn("px-4 lg:px-6 py-4 shrink-0", className)}>
      {children}
    </div>
  );
};

/**
 * A page toolbar (DD-058). From md up it is one line: the first child (the
 * page's tab group; Reporting's period title) on the left, the rest on the
 * right. Below md it stacks: the first child on its own full-width strip,
 * the other children (the page's Actions) on full-width rows below.
 *
 * The strip is a one-column grid sized to the child's max-content width, so
 * a tab group too wide for the phone keeps its whole labels and scrolls
 * sideways inside the strip, instead of squeezing them (TabGroup's tabs are
 * min-w-0 with truncating labels) or running past the screen edge. From md
 * up the strip is display: contents and the child is the row's flex item,
 * exactly as before.
 *
 * Below md the column never wraps, whatever the page passes (Finance passes
 * flex-wrap for its desktop row): a wrapping column sizes its line to its
 * widest child, which would widen the strip and the actions past the screen.
 *
 * The selected tab starts in view: the effect sets the strip's own
 * scrollLeft (never scrollIntoView, which would scroll the shells too) on
 * mount, whenever the strip or the tab group resizes (Leads' counts load late
 * and widen the labels; the phone turns), and whenever the selection changes.
 * Once the person scrolls the strip, their scroll wins over a resize, but a
 * new selection always wins over their scroll: the tab they picked, or the
 * one Back or the burger landed on, ends whole in view.
 */
const Row: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const [lead, ...rest] = React.Children.toArray(children);
  const stripRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    let touched = false;
    const reveal = (force: boolean) => {
      // A display: contents strip (md up) has no client size.
      if ((touched && !force) || strip.clientWidth === 0) return;
      const tab = strip.querySelector<HTMLElement>('[aria-current="true"]');
      if (!tab) return;
      const stripBox = strip.getBoundingClientRect();
      const tabBox = tab.getBoundingClientRect();
      const pastRight = tabBox.right > stripBox.right;
      if (!pastRight && tabBox.left >= stripBox.left) return;
      if (!tab.nextElementSibling) {
        // The track's last tab: show the track's padding and rounded end too.
        strip.scrollLeft = strip.scrollWidth - strip.clientWidth;
      } else if (!tab.previousElementSibling) {
        strip.scrollLeft = 0;
      } else if (pastRight) {
        // WebKit truncates scrollLeft to an integer: round away from the tab so it ends whole.
        strip.scrollLeft += Math.ceil(tabBox.right - stripBox.right);
      } else {
        strip.scrollLeft -= Math.ceil(stripBox.left - tabBox.left);
      }
    };
    const stop = () => {
      touched = true;
    };
    reveal(false);
    strip.addEventListener("pointerdown", stop, { passive: true });
    strip.addEventListener("touchstart", stop, { passive: true });
    strip.addEventListener("wheel", stop, { passive: true });
    const group = strip.firstElementChild;
    const resizes =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => reveal(false)) : null;
    resizes?.observe(strip);
    if (group) resizes?.observe(group);
    const selections = new MutationObserver(() => reveal(true));
    selections.observe(strip, { subtree: true, attributes: true, attributeFilter: ["aria-current"] });
    return () => {
      resizes?.disconnect();
      selections.disconnect();
      strip.removeEventListener("pointerdown", stop);
      strip.removeEventListener("touchstart", stop);
      strip.removeEventListener("wheel", stop);
    };
  }, []);

  return (
    <div
      className={cn(
        "flex flex-col items-stretch gap-3 max-md:flex-nowrap md:flex-row md:items-center md:justify-between",
        className,
      )}
    >
      {lead !== undefined && (
        <div
          ref={stripRef}
          data-page-header-tabs=""
          className="grid grid-flow-col auto-cols-max overflow-x-auto md:contents"
        >
          {lead}
        </div>
      )}
      {rest}
    </div>
  );
};

const Title: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <h1 className={cn("text-xl lg:text-2xl font-semibold text-gray-900", className)}>
      {children}
    </h1>
  );
};

const Subtitle: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <p className={cn("text-sm text-gray-500 mt-1", className)}>
      {children}
    </p>
  );
};

/**
 * A toolbar's actions (DD-058). Below md they fill the row under the tab
 * strip: a single action sits at the right edge, several spread with space
 * between, and actions that outgrow the row wrap whole rather than breaking
 * their labels. From md up they pack together at the row's right end, as
 * before.
 */
const Actions: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <div
      data-page-header-actions=""
      className={cn(
        "flex items-center gap-3 shrink-0",
        "flex-wrap justify-between [&>:only-child]:ml-auto",
        "md:flex-nowrap md:justify-start md:[&>:only-child]:ml-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

const Tabs: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <div className={cn("mt-4 inline-block", className)}>
      {children}
    </div>
  );
};

export const PageHeader = Object.assign(PageHeaderRoot, {
  Row,
  Title,
  Subtitle,
  Actions,
  Tabs,
});
